import type {
  RequestCookies,
  ResponseCookies,
} from 'next/dist/compiled/@edge-runtime/cookies';
import type { ReadonlyRequestCookies } from 'next/dist/server/web/spec-extension/adapters/request-cookies';

import type { ReferrerSourceCodeCookie } from '@sola/schema/dotcom/checkout/cookies.ts';
import type {
  CampaignCookie,
  CookieProps,
} from '@sola/schema/dotcom/shared/cookies.ts';
import { capitalize } from '@sola/utils/string.ts';

import {
  COOKIE_COMPASSION_DOMAIN,
  COOKIE_NAME_CAMPAIGN,
  COOKIE_NAME_CAMPAIGN_LE,
  COOKIE_NAME_REFERRER_SOURCE_CODE,
} from './constants.ts';
import { base64DecodeObject, base64UrlEncodeObject } from './misc.ts';

export type ParsedCookies = Record<string, string>;

export const getMaxAgeFromDays = (days: number) => days * 24 * 60 * 60;

const splitOnFirstInstance =
  (char: string) =>
  (str: string): string[] => {
    const eqPos = str.indexOf(char);
    if (eqPos === -1) {
      return [str, ''];
    }

    const before = str.slice(0, Math.max(0, eqPos));
    const after = str.slice(Math.max(0, eqPos + 1));
    return [before, after];
  };

export const parseCookies = (cookies: string[] = []): ParsedCookies => {
  return Object.fromEntries(
    cookies
      .map(splitOnFirstInstance('='))
      .map(([k, v]) => [k, decodeURIComponent(v)]),
  );
};

export const getCookie = (
  cookieName: string,
  cookies: string[] | ReadonlyRequestCookies | RequestCookies | ParsedCookies,
) => {
  // lambda/ApiGateway cookie
  if (Array.isArray(cookies)) {
    return parseCookies(cookies)[cookieName];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof (cookies as any)?.get === 'function') {
    // Next.js cookie
    return (cookies as ReadonlyRequestCookies).get(cookieName)?.value;
  }

  return (cookies as ParsedCookies)?.[cookieName];
};

export const setCookie = (
  cookie: CookieProps,
  cookies?:
    | string[]
    | ReadonlyRequestCookies
    | RequestCookies
    | ResponseCookies,
) => {
  const {
    name,
    value,
    domain,
    maxAge, // Using Max-Age over Expires as it's more dependable
    path = '/',
    httpOnly = true,
    sameSite = 'strict',
  } = cookie;

  // Set lambda/ApiGateway cookie
  if (!cookies || Array.isArray(cookies)) {
    // Return cookie string
    return `${name}=${value};`.concat(
      domain ? `Domain=${domain};` : '',
      maxAge ? `Max-Age=${maxAge};` : '',
      `Path=${path};`,
      httpOnly ? 'HttpOnly;' : '',
      `SameSite=${capitalize(sameSite)};`,
      'Secure',
    );
  }

  // Set Next.js cookie
  cookies.set(name, value, {
    domain,
    ...(maxAge && {
      maxAge,
    }),
    path,
    httpOnly,
    sameSite,
    secure: true,
  });

  // Returning a string for auth's purposes
  return 'true';
};

export const removeCookie = (
  cookie: CookieProps,
  cookies?: string[] | ReadonlyRequestCookies | RequestCookies,
) => {
  return setCookie(
    {
      ...cookie,
      maxAge: -1,
    },
    cookies,
  );
};

export const parseDecodedStringToObject = <T>(initialStr: string): T => {
  // First rip out cookie properties:
  const value = initialStr.includes(';')
    ? initialStr.split(';')[0]
    : initialStr;
  return value.split('&').reduce((acc, pair: string) => {
    const [key, val] = pair.split('=');
    acc[key as keyof T] = val as T[keyof T];
    return acc;
  }, {} as T);
};

export const parseCookieToObject = <T>(str: string): T => {
  return parseDecodedStringToObject<T>(decodeURIComponent(str));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseObjectToCookieString = <T extends Record<string, any>>(
  obj: T,
): string => {
  return Object.entries(obj)
    .map(([key, value]) => {
      const encodedKey = key;
      const encodedValue = String(value);
      return `${encodedKey}=${encodedValue}`;
    })
    .join('&');
};

/**
 * Gets the campaign cookie from the request cookies and returns it as an object.
 */
export const getCampaignCookie = (
  reqCookies: string[],
): CampaignCookie | null => {
  if (!Array.isArray(reqCookies)) {
    return null;
  }

  // Find campaign cookie directly without parsing all reqCookies
  const campaignCookieHeader = reqCookies.find((cookie) =>
    cookie.includes(COOKIE_NAME_CAMPAIGN),
  );
  if (!campaignCookieHeader) {
    return null;
  }

  const cookies = parseCookies([campaignCookieHeader]);
  const campaignCookie = cookies[COOKIE_NAME_CAMPAIGN];

  if (!campaignCookie) {
    return null;
  }

  return parseDecodedStringToObject<CampaignCookie>(campaignCookie);
};

export const setCampaignCookie = (
  cookieValue: CampaignCookie,
  cookies?: ReadonlyRequestCookies | RequestCookies | ResponseCookies,
): string => {
  const cookieValueString = parseObjectToCookieString(cookieValue);
  return (
    setCookie(
      {
        name: COOKIE_NAME_CAMPAIGN,
        value: cookieValueString,
        domain: COOKIE_COMPASSION_DOMAIN,
        sameSite: 'none',
        maxAge: getMaxAgeFromDays(90),
      },
      cookies,
    ) || ''
  );
};

// Set the live event campaign cookie
export const setCampaignLeCookie = (
  cookieValue: number,
  cookies?: ReadonlyRequestCookies | RequestCookies | ResponseCookies,
): string => {
  return (
    setCookie(
      {
        name: COOKIE_NAME_CAMPAIGN_LE,
        value: cookieValue.toString(),
        domain: process.env.COOKIE_DOMAIN_STAGE,
        httpOnly: false,
        maxAge: getMaxAgeFromDays(3),
      },
      cookies,
    ) || ''
  );
};

export const getCookieCampaignId = (
  cookies: string | string[],
): string | undefined => {
  if (!cookies) {
    return undefined;
  }

  const cookieArray = Array.isArray(cookies)
    ? cookies
    : cookies.replaceAll(' ', '').split(';');

  const campaignCookie = getCampaignCookie(cookieArray);
  return campaignCookie?.ExternalId;
};

export const parseRsc = (value?: string) => {
  return (
    base64DecodeObject<ReferrerSourceCodeCookie>(value) ??
    ({} as ReferrerSourceCodeCookie)
  );
};

export const parseRscCookie = (
  cookies: ReadonlyRequestCookies | RequestCookies | ParsedCookies,
) => {
  const cookieValue = getCookie(COOKIE_NAME_REFERRER_SOURCE_CODE, cookies);

  return parseRsc(cookieValue);
};

export const getEncodedRsc = (rsc: ReferrerSourceCodeCookie) => {
  return base64UrlEncodeObject(rsc);
};

export const setRscCookie = (
  rscCookie: ReferrerSourceCodeCookie,
  cookies: ReadonlyRequestCookies | RequestCookies | ResponseCookies,
) => {
  setCookie(
    {
      name: COOKIE_NAME_REFERRER_SOURCE_CODE,
      value: getEncodedRsc(rscCookie),
      domain: COOKIE_COMPASSION_DOMAIN,
    },
    cookies,
  );
};

export const getDismissedBannerIds = (cookieName: string): string[] => {
  try {
    const cookies = document.cookie;
    const match = cookies
      .split('; ')
      .find((row) => row.startsWith(`${cookieName}=`));

    if (!match) {
      return [];
    }

    const value = decodeURIComponent(match.split('=')[1] ?? '');

    if (!value) {
      return [];
    }

    return value.split('|').filter((id) => id !== '');
  } catch {
    // document.cookie may throw in sandboxed iframes or restricted environments
    return [];
  }
};

export const addDismissedBannerId = (
  cookieName: string,
  bannerId: string,
): void => {
  if (!bannerId) {
    return;
  }

  const current = getDismissedBannerIds(cookieName);

  if (current.includes(bannerId)) {
    return;
  }

  const updated = [...current, bannerId].join('|');

  try {
    // eslint-disable-next-line unicorn/no-document-cookie -- intentional client-side cookie write for banner dismissal persistence
    document.cookie = `${cookieName}=${encodeURIComponent(updated)}; path=/; SameSite=Lax; Secure`;
  } catch {
    // document.cookie may not be writable in sandboxed environments
  }
};
